import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import CategoryGrid from '@/pages/home/components/CategoryGrid';
import TrendingPanel from '@/pages/home/components/TrendingPanel';
import NewsPreview from '@/pages/home/components/NewsPreview';
import SpecialPreview from '@/pages/home/components/SpecialPreview';
import SectionHeading from '@/components/base/SectionHeading';
import MovieCard from '@/components/base/MovieCard';
import PlaybackModal from '@/components/base/PlaybackModal';
import { allMovies, allNews, getFeaturedMovies, getLatestMovies, getTopRatedMovies, getTrendingMovies } from '@/lib/catalog';
import { specials } from '@/mocks/specials';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import type { Movie } from '@/types';

export default function Home() {
  const [playMovie, setPlayMovie] = useState<Movie | null>(null);

  useDocumentMeta({
    title: '午夜电影网 - 高清电影在线播放与最新电影资讯',
    description:
      '午夜电影网提供丰富的高清电影资源和最新影视资讯，覆盖多种类型电影，满足用户在线观看和了解电影动态的需求。',
    keywords: '午夜电影网,电影在线看,高清电影,影视资讯,最新电影,电影下载,在线电影,电影推荐',
  });

  const featured = getFeaturedMovies();
  const latest = getLatestMovies(6);
  const trending = getTrendingMovies(6);
  const topRated = getTopRatedMovies(3);

  return (
    <Fragment>
      <HeroCarousel movies={featured} onPlay={setPlayMovie} />

      <section className="border-y border-background-200 bg-background-100">
        <div className="shell grid grid-cols-2 gap-6 py-7 md:grid-cols-4 md:py-8">
          {[
            { value: `${allMovies.length}`, label: '精选影片资料', icon: 'ri-film-line' },
            { value: `${allNews.length}`, label: '深度影视资讯', icon: 'ri-newspaper-line' },
            { value: `${specials.length}`, label: '主题策展专题', icon: 'ri-layout-grid-line' },
            { value: '100%', label: '人工筛选与推荐', icon: 'ri-award-line' },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-200 text-primary-500">
                <i className={`${stat.icon} text-lg`} aria-hidden="true"></i>
              </span>
              <div>
                <p className="font-heading text-2xl leading-none text-foreground-950">{stat.value}</p>
                <p className="mt-1 text-[11px] text-foreground-500 md:text-xs">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="shell">
          <SectionHeading
            eyebrow="Browse by genre"
            title="分类快捷入口"
            description="从类型出发，快速进入你想看的世界。动作、科幻、爱情、悬疑，每一种情绪都有对应的深夜。"
            actionLabel="全部电影"
            actionTo="/movies"
          />
          <div className="mt-8">
            <CategoryGrid />
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-14 md:py-20">
        <div className="shell">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <SectionHeading
                eyebrow="Now streaming"
                title="最新电影推荐"
                description="按上线时间整理的近期片单，覆盖院线新片与口碑之作。"
                actionLabel="进入片库"
                actionTo="/movies"
              />
              <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5">
                {latest.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
            <div className="lg:col-span-4">
              <TrendingPanel movies={trending} />
              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <p className="eyebrow text-[10px] text-primary-500">Tonight&apos;s pick</p>
                <h3 className="mt-1 font-heading text-xl tracking-wide text-foreground-950">今夜编辑推荐</h3>
                <p className="mt-2 text-xs leading-6 text-foreground-500">
                  如果你只想随便点开一部，我们希望是《父亲的胶片》。它安静、克制，却会在最后十分钟让你想起某个人。
                </p>
                <Link
                  to="/movie/fathers-film"
                  className="mt-4 inline-flex items-center gap-1 text-xs text-primary-500 transition-colors hover:text-primary-600"
                >
                  去看这部
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg tracking-wide text-foreground-950">口碑高分</h3>
                  <Link to="/movies?sort=rating" className="text-[10px] text-foreground-500 transition-colors hover:text-primary-500">
                    完整榜单
                  </Link>
                </div>
                <ul className="mt-3 space-y-2.5">
                  {topRated.map((movie, index) => (
                    <li key={movie.id}>
                      <Link to={`/movie/${movie.id}`} className="group flex items-center gap-3">
                        <span className="w-4 font-heading text-sm text-foreground-500">{String(index + 1).padStart(2, '0')}</span>
                        <span className="min-w-0 flex-1 truncate text-xs text-foreground-700 transition-colors group-hover:text-primary-500">
                          {movie.title}
                        </span>
                        <span className="flex shrink-0 items-center gap-0.5 text-[11px] text-primary-500">
                          <i className="ri-star-fill text-[9px]" aria-hidden="true"></i>
                          {movie.rating.toFixed(1)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-accent-100 py-14 md:py-20">
        <div className="shell">
          <SectionHeading
            eyebrow="Curated specials"
            title="专题与策展"
            description="不是榜单，是主题。我们把气质相近的电影放在一起，让你一次看个够。"
            actionLabel="全部专题"
            actionTo="/specials"
          />
          <div className="mt-8">
            <SpecialPreview items={specials.slice(0, 3)} />
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="shell">
          <SectionHeading
            eyebrow="Latest news"
            title="最新电影资讯"
            description="影评、幕后、业内动态与奖项观察，帮你看懂银幕之外发生的事。"
            actionLabel="全部资讯"
            actionTo="/news"
          />
          <div className="mt-8">
            <NewsPreview items={allNews.slice(0, 4)} />
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="shell">
          <div className="relative overflow-hidden rounded-lg border border-background-200 bg-background-100 px-6 py-10 md:px-12 md:py-14">
            <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl" />
            <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <p className="eyebrow text-[11px] text-primary-500">Find your next film</p>
                <h2 className="mt-2 font-heading text-3xl tracking-wide text-foreground-950 md:text-4xl">
                  还没决定今晚看什么？
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-500">
                  用关键词搜索片名、导演或演员，也可以直接从片库的筛选器里，按年份、地区和评分慢慢挑。
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/search"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-search-line" aria-hidden="true"></i>
                  搜索电影
                </Link>
                <Link
                  to="/movies"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-500/70 hover:text-primary-500"
                >
                  <i className="ri-play-list-2-line" aria-hidden="true"></i>
                  浏览片库
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PlaybackModal open={Boolean(playMovie)} movie={playMovie ?? undefined} onClose={() => setPlayMovie(null)} />
    </Fragment>
  );
}